import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { REG } from "../constants/testIds3";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, ThumbsUp, ThumbsDown } from "@phosphor-icons/react";

const REQUEST_TYPES = [
  { v: "missed_check_in", l: "Missed check-in" },
  { v: "missed_check_out", l: "Missed check-out" },
  { v: "wrong_time", l: "Wrong time" },
  { v: "off_site_approval", l: "Off-site approval" },
];

const STATUS = {
  pending: "bg-amber-50 text-amber-800 border-amber-200",
  approved: "bg-emerald-50 text-emerald-800 border-emerald-200",
  rejected: "bg-red-50 text-red-800 border-red-200",
};

export default function Regularization() {
  const { user, tenantId } = useAuth();
  const canReview = ["super_admin", "company_admin", "hr", "manager"].includes(user?.role);
  const [mine, setMine] = useState([]);
  const [all, setAll] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ date: "", request_type: "missed_check_in", proposed_check_in_at: "", proposed_check_out_at: "", reason: "" });
  const [err, setErr] = useState("");

  const load = () => {
    if (user?.employee_id) api.get("/regularizations/mine").then(({ data }) => setMine(data));
    if (canReview) {
      const params = user?.role === "super_admin" && tenantId ? { tenant_id: tenantId } : {};
      api.get("/regularizations", { params }).then(({ data }) => setAll(data));
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tenantId, user?.role]);

  const submit = async () => {
    setErr("");
    try {
      const payload = {
        date: form.date, request_type: form.request_type, reason: form.reason,
      };
      if (form.proposed_check_in_at) payload.proposed_check_in_at = new Date(form.proposed_check_in_at).toISOString();
      if (form.proposed_check_out_at) payload.proposed_check_out_at = new Date(form.proposed_check_out_at).toISOString();
      await api.post("/regularizations", payload);
      toast.success("Regularization request submitted");
      setOpen(false); setForm({ date: "", request_type: "missed_check_in", proposed_check_in_at: "", proposed_check_out_at: "", reason: "" });
      load();
    } catch (e) { setErr(formatApiError(e)); }
  };

  const decide = async (id, status) => {
    try { await api.patch(`/regularizations/${id}`, { status }); toast.success(`Request ${status}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-emerald-700">Time</div>
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight">Attendance regularization</h1>
          <p className="text-sm text-zinc-500 mt-2">Correct missed or incorrect attendance entries — manager approves.</p>
        </div>
        {user?.employee_id && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="btn-zenith rounded-sm" data-testid={REG.createBtn}><Plus size={16} className="mr-1.5" /> Request correction</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-display text-2xl">Request attendance correction</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2 space-y-1"><Label className="text-xs">Date</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="rounded-sm" data-testid="reg-date-input" /></div>
                <div className="col-span-2 space-y-1"><Label className="text-xs">Request type</Label>
                  <Select value={form.request_type} onValueChange={(v) => setForm({ ...form, request_type: v })}>
                    <SelectTrigger className="rounded-sm" data-testid="reg-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{REQUEST_TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1"><Label className="text-xs">Proposed check-in</Label><Input type="datetime-local" value={form.proposed_check_in_at} onChange={(e) => setForm({ ...form, proposed_check_in_at: e.target.value })} className="rounded-sm" /></div>
                <div className="space-y-1"><Label className="text-xs">Proposed check-out</Label><Input type="datetime-local" value={form.proposed_check_out_at} onChange={(e) => setForm({ ...form, proposed_check_out_at: e.target.value })} className="rounded-sm" /></div>
                <div className="col-span-2 space-y-1"><Label className="text-xs">Reason *</Label><Textarea value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className="rounded-sm" placeholder="Client meeting overran, forgot to check out…" data-testid="reg-reason-input" /></div>
              </div>
              {err && <div className="text-sm text-red-600 border border-red-200 bg-red-50 px-3 py-2 rounded-sm">{err}</div>}
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
                <Button onClick={submit} className="btn-zenith rounded-sm" data-testid={REG.saveBtn}>Submit</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {user?.employee_id && (
        <section>
          <h2 className="text-xs uppercase tracking-widest text-zinc-500 mb-2">My requests</h2>
          <div className="overflow-x-auto border border-zinc-200 rounded-sm">
            <table className="hr-table" data-testid={REG.myList}>
              <thead><tr><th>Date</th><th>Type</th><th>Proposed in</th><th>Proposed out</th><th>Reason</th><th>Status</th></tr></thead>
              <tbody>
                {mine.length === 0 && <tr><td colSpan={6} className="text-center py-6 text-zinc-500 text-sm">No requests yet.</td></tr>}
                {mine.map((r) => (
                  <tr key={r.id}>
                    <td className="font-mono-plex text-xs">{r.date}</td>
                    <td className="text-xs">{REQUEST_TYPES.find((t) => t.v === r.request_type)?.l}</td>
                    <td className="font-mono-plex text-xs">{r.proposed_check_in_at ? new Date(r.proposed_check_in_at).toLocaleTimeString() : "—"}</td>
                    <td className="font-mono-plex text-xs">{r.proposed_check_out_at ? new Date(r.proposed_check_out_at).toLocaleTimeString() : "—"}</td>
                    <td className="text-xs text-zinc-600 max-w-[220px] truncate">{r.reason}</td>
                    <td><Badge variant="outline" className={`rounded-sm ${STATUS[r.status] || ""}`}>{r.status}</Badge></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {canReview && (
        <section>
          <h2 className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Team requests</h2>
          <div className="overflow-x-auto border border-zinc-200 rounded-sm">
            <table className="hr-table" data-testid={REG.adminList}>
              <thead><tr><th>Employee</th><th>Date</th><th>Type</th><th>Reason</th><th>Status</th><th>Actions</th></tr></thead>
              <tbody>
                {all.length === 0 && <tr><td colSpan={6} className="text-center py-6 text-zinc-500 text-sm">Nothing to review.</td></tr>}
                {all.map((r) => (
                  <tr key={r.id}>
                    <td><div className="text-sm">{r.employee_name}</div><div className="text-xs font-mono-plex text-zinc-500">{r.employee_code}</div></td>
                    <td className="font-mono-plex text-xs">{r.date}</td>
                    <td className="text-xs">{REQUEST_TYPES.find((t) => t.v === r.request_type)?.l}</td>
                    <td className="text-xs text-zinc-600 max-w-[260px] truncate">{r.reason}</td>
                    <td><Badge variant="outline" className={`rounded-sm ${STATUS[r.status] || ""}`}>{r.status}</Badge></td>
                    <td>
                      {r.status === "pending" ? (
                        <div className="flex gap-1">
                          <button data-testid={REG.approveBtn(r.id)} onClick={() => decide(r.id, "approved")} className="text-xs px-2 py-1 border border-emerald-300 text-emerald-800 hover:bg-emerald-50 rounded-sm"><ThumbsUp size={12} className="inline mr-1" /> Approve</button>
                          <button data-testid={REG.rejectBtn(r.id)} onClick={() => decide(r.id, "rejected")} className="text-xs px-2 py-1 border border-red-300 text-red-700 hover:bg-red-50 rounded-sm"><ThumbsDown size={12} className="inline mr-1" /> Reject</button>
                        </div>
                      ) : <span className="text-xs text-zinc-400">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}
